import React, { useCallback, useEffect, useState } from "react";
import { Award, Users, Building2, CalendarCheck, RefreshCw, Ban, Search } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CertificateActions } from "@/components/certs/CertificateActions";
import { location } from "@/lib/certs";

export default function Certificates() {
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (type !== "all") p.set("type", type);
    if (status !== "all") p.set("status", status);
    api.get(`/certificates?${p}`).then(({ data }) => setRows(data));
    api.get("/certificates/stats").then(({ data }) => setStats(data));
  }, [q, type, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <PageHeader title="Certificates" subtitle="Onboarding certificates for Delivery Partners and Franchise Partners" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="cert-stats">
        <KpiCard label="Total Issued" value={stats.total ?? 0} icon={Award} accent="primary" />
        <KpiCard label="Delivery Partner" value={stats.dp ?? 0} icon={Users} accent="cyan" />
        <KpiCard label="Franchise Partner" value={stats.franchise ?? 0} icon={Building2} accent="violet" />
        <KpiCard label="Issued Today" value={stats.today ?? 0} icon={CalendarCheck} accent="emerald" />
        <KpiCard label="Reissued" value={stats.reissued ?? 0} icon={RefreshCw} accent="amber" />
        <KpiCard label="Revoked" value={stats.revoked ?? 0} icon={Ban} accent="rose" />
      </div>
      <div className="mt-6 flex flex-col gap-2 md:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, Connect Tym ID, Franchise ID, certificate no., city or state" className="pl-9" data-testid="cert-search-input" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="md:w-48" data-testid="cert-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All partner types</SelectItem><SelectItem value="dp">Delivery Partner</SelectItem><SelectItem value="franchise">Franchise Partner</SelectItem></SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="md:w-40" data-testid="cert-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="Valid">Valid</SelectItem><SelectItem value="Reissued">Reissued</SelectItem><SelectItem value="Revoked">Revoked</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-card">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Certificate No.</TableHead><TableHead>Partner Name</TableHead><TableHead>Partner Type</TableHead><TableHead>Partner ID</TableHead>
            <TableHead>Location</TableHead><TableHead>Issue Date</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground" data-testid="cert-empty">No certificates found. Certificates are created automatically when a partner is onboarded as Active.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`cert-row-${r.certificate_number}`}>
                <TableCell className="font-mono text-xs">{r.certificate_number}</TableCell>
                <TableCell className="max-w-[220px] truncate font-semibold">{r.partner_name}</TableCell>
                <TableCell className="text-xs">{r.partner_type_label}</TableCell>
                <TableCell className="font-mono text-xs">{r.partner_code}</TableCell>
                <TableCell className="text-xs">{location(r)}</TableCell>
                <TableCell className="whitespace-nowrap text-xs">{fmtDate(r.issue_date)}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell><CertificateActions cert={r} onChanged={load} labels={false} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
